/**
 * Business Dashboard Page
 *
 * A comprehensive dashboard for business owners to manage their listings,
 * view analytics, manage promotions, and respond to reviews.
 *
 * Only accessible to users with the Business Owner subscription.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faStore,
  faPlus,
  faChartLine,
  faStar,
  faCoins,
  faEdit,
  faEye,
  faExclamationTriangle,
  faCheckCircle,
  faClock,
  faArrowRight,
  faSync,
  faBullhorn,
  faCommentDots,
  faRocket,
  faAnchor,
  faIdBadge,
  faGift,
  faChartBar,
  faHandHoldingHeart,
  faCalendarAlt,
  faCalendarPlus,
  faUniversity,
  faReceipt,
} from '@fortawesome/free-solid-svg-icons';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, onSnapshot, collection, query, where, getCountFromServer } from 'firebase/firestore';
import { firestore } from '../FirebaseConfig';
import { useAuth } from '../contexts/CombinedAuthContext';
import { usePlans } from '../contexts/PlansContext';
import { BusinessListingsApi } from '../services/FirebaseApiService';
import {
  OwnerDashboard,
  OwnerActions,
  PerformanceSummary,
  ListingTips,
} from '../components/BusinessOwnerFeatures';
import PromotionStackPanel from '../components/business/PromotionStackPanel';
import { BusinessOwnerReviews } from '../components/BusinessOwnerReviews';
import StaffAdmin from '../components/business/StaffAdmin';
import RedeemableItemsAdmin from '../components/business/RedeemableItemsAdmin';
import EventsManager from '../components/business/EventsManager';
import PaymentsManager from '../components/business/PaymentsManager';
import BookingsManager from '../components/business/BookingsManager';
import PromotionsAnalytics from '../components/PromotionsAnalytics';
import RedemptionAnalytics from '../components/RedemptionAnalytics';
import { useToast } from '../components/ui';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { PageShell } from '../components/ui/PageShell';
import '../styles/BusinessDashboard.css';

const functions = getFunctions(undefined, 'us-central1');

const STATUS_CONFIG = {
  active:   { color: 'success', icon: faCheckCircle,      label: 'Active'    },
  pending:  { color: 'warning', icon: faClock,            label: 'Pending'   },
  rejected: { color: 'danger',  icon: faExclamationTriangle, label: 'Rejected' },
  inactive: { color: 'neutral', icon: faClock,            label: 'Inactive'  },
  archived: { color: 'neutral', icon: faClock,            label: 'Archived'  },
};

const PROMOTION_LABELS = {
  premium:   '⚓ Premium',
  featured:  '★ Featured',
  spotlight: 'Spotlight',
  boost:     'Trending',
};

const DASHBOARD_TABS = new Set([
  'overview',
  'promotion',
  'promotions-analytics',
  'redemption-analytics',
  'reviews',
  'redeemables',
  'events',
  'staff',
  'payments',
  'bookings',
  'actions',
]);

const getTabFromSearch = (search) => {
  const params = new URLSearchParams(search || '');
  const tab = params.get('tab');
  return tab && DASHBOARD_TABS.has(tab) ? tab : 'overview';
};

/**
 * Listing Card in the sidebar
 */
const DashboardListingCard = ({ listing, onSelect, isSelected, onEdit, onAddEvent }) => {
  const status = STATUS_CONFIG[listing.status] || STATUS_CONFIG.inactive;
  const isPromoted = listing.promotion?.isActive && listing.promotion?.expiresAt && new Date(listing.promotion.expiresAt) > new Date();
  const [eventCount, setEventCount] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getCountFromServer(
          query(collection(firestore, 'events'), where('businessId', '==', listing.id))
        );
        if (!cancelled) setEventCount(snap.data().count);
      } catch {
        if (!cancelled) setEventCount(0);
      }
    })();
    return () => { cancelled = true; };
  }, [listing.id]);

  return (
    <div
      className={`dashboard-listing-card ${isSelected ? 'selected' : ''}`}
      onClick={() => onSelect(listing)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onSelect(listing)}
    >
      <div className="listing-card-image">
        {listing.images && listing.images.length > 0 ? (
          <img src={listing.images[0]} alt={listing.name} />
        ) : (
          <div className="listing-card-placeholder">
            <FontAwesomeIcon icon={faStore} />
          </div>
        )}
        <span className={`listing-status-badge status-${status.color}`}>
          <FontAwesomeIcon icon={status.icon} />
          {status.label}
        </span>
        {isPromoted && (
          <span className="listing-promotion-badge">
            {PROMOTION_LABELS[listing.promotion.type] || 'Promoted'}
          </span>
        )}
      </div>
      <div className="listing-card-content">
        <h3 className="listing-card-name">{listing.name}</h3>
        <p className="listing-card-category">{listing.category || 'Uncategorized'}</p>
        <div className="listing-card-stats">
          <span className="stat">
            <FontAwesomeIcon icon={faEye} />
            {listing.viewCount || 0} views
          </span>
          <span className="stat">
            <FontAwesomeIcon icon={faStar} />
            {listing.averageRating?.toFixed(1) || '-'}
          </span>
          <span className="stat" title="Events attached to this listing">
            <FontAwesomeIcon icon={faCalendarAlt} />
            {eventCount === null ? '…' : `${eventCount} event${eventCount === 1 ? '' : 's'}`}
          </span>
        </div>
        {isPromoted && (
          <div className="listing-card-promotion" title="Active promotion">
            <FontAwesomeIcon icon={faBullhorn} />
            <span>{PROMOTION_LABELS[listing.promotion.type] || 'Promoted'}</span>
          </div>
        )}
      </div>
      <div className="listing-card-actions">
        <button
          className="btn-icon btn-edit-listing"
          onClick={(e) => { e.stopPropagation(); onEdit(listing); }}
          title="Edit Listing"
        >
          <FontAwesomeIcon icon={faEdit} />
          <span>Edit Listing</span>
        </button>
        {onAddEvent && (
          <button
            className="btn-icon btn-add-event"
            onClick={(e) => { e.stopPropagation(); onAddEvent(listing); }}
            title="Add Event"
          >
            <FontAwesomeIcon icon={faCalendarPlus} />
            <span>Add Event</span>
          </button>
        )}
      </div>
    </div>
  );
};

const ListingCardSkeleton = () => (
  <div className="dashboard-listing-card skeleton">
    <div className="listing-card-image">
      <Skeleton width="100%" height="120px" />
    </div>
    <div className="listing-card-content">
      <Skeleton width="80%" height="20px" />
      <Skeleton width="60%" height="16px" />
      <Skeleton width="100%" height="24px" />
    </div>
  </div>
);

/**
 * Main Business Dashboard Component
 */
const BusinessDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { firebaseUser, isAuthenticated } = useAuth();
  const { isBusinessMember, loading: plansLoading } = usePlans();
  const { showToast } = useToast();

  const [listings, setListings]               = useState([]);
  const [selectedListing, setSelectedListing] = useState(null);
  const [loading, setLoading]                 = useState(true);
  const [refreshing, setRefreshing]           = useState(false);
  const [activeTab, setActiveTab]             = useState(() => getTabFromSearch(location.search));
  const [error, setError]                     = useState(null);
  const [tokenBalance, setTokenBalance]       = useState(0);
  const [eventEditorPrefill, setEventEditorPrefill] = useState(null);

  const handleTabChange = useCallback((tabKey) => {
    if (!DASHBOARD_TABS.has(tabKey)) return;
    setActiveTab(tabKey);
    navigate(`/business-dashboard?tab=${tabKey}`, { replace: true });
  }, [navigate]);

  useEffect(() => {
    const tabFromUrl = getTabFromSearch(location.search);
    setActiveTab((prev) => (prev === tabFromUrl ? prev : tabFromUrl));
  }, [location.search]);

  // Subscribe to the user's token balance in real time
  useEffect(() => {
    if (!firebaseUser?.uid) return;
    const userRef = doc(firestore, 'users', firebaseUser.uid);
    const unsub = onSnapshot(userRef, (snap) => {
      if (snap.exists()) setTokenBalance(snap.data().tokenBalance || 0);
    });
    return unsub;
  }, [firebaseUser?.uid]);

  // Fetch user's listings
  const fetchListings = useCallback(async (showRefreshIndicator = false) => {
    const userId = firebaseUser?.uid;
    if (!userId) { setLoading(false); return; }

    showRefreshIndicator ? setRefreshing(true) : setLoading(true);
    setError(null);

    try {
      const userListings = await BusinessListingsApi.getByOwner(userId);
      setListings(userListings || []);
      if (userListings?.length > 0) {
        setSelectedListing((prev) => prev ? userListings.find(l => l.id === prev.id) || userListings[0] : userListings[0]);
      }
    } catch (err) {
      console.error('Error fetching listings:', err);
      setError('Failed to load your listings. Please try again.');
      showToast('Failed to load listings', 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [firebaseUser?.uid, showToast]);

  useEffect(() => {
    if (plansLoading) return;
    if (isAuthenticated && firebaseUser?.uid) {
      fetchListings();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, plansLoading, firebaseUser?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Listing management handlers ---

  const handleSelectListing = (listing) => {
    // Keep the current active tab so the user can browse the same view
    // (Promotion, Events, Staff, etc.) across their different listings.
    setSelectedListing(listing);
  };

  const handleEditListing = (listing) => navigate(`/edit-business-listing/${listing.id}`);

  const handleAddEvent = (listing) => {
    setSelectedListing(listing);
    setEventEditorPrefill({ mode: 'create', listingId: listing.id, listingName: listing.name });
    handleTabChange('events');
  };

  const handleCreateListing = () => navigate('/edit-business-listing/new');

  const handleRefresh = () => fetchListings(true);

  const handleDuplicate = async (listing) => {
    try {
      const fn = httpsCallable(functions, 'duplicateBusinessListing');
      const result = await fn({ id: listing.id });
      showToast('Listing duplicated — editing the copy now.', 'success');
      navigate(`/edit-business-listing/${result.data.id}`);
    } catch (err) {
      showToast(err.message || 'Failed to duplicate listing.', 'error');
    }
  };

  const handleArchive = async (listing) => {
    try {
      const fn = httpsCallable(functions, 'archiveBusinessListing');
      await fn({ id: listing.id });
      showToast('Listing archived.', 'success');
      setSelectedListing(null);
      fetchListings(true);
    } catch (err) {
      showToast(err.message || 'Failed to archive listing.', 'error');
    }
  };

  const handleUpgradeTokens = () => navigate('/tokens');

  // --- Loading / access gates ---

  if (plansLoading) {
    return (
      <PageShell width="full" className="business-dashboard">
        <div className="dashboard-loading">
          <FontAwesomeIcon icon={faAnchor} spin size="3x" />
          <p>Loading your dashboard...</p>
        </div>
      </PageShell>
    );
  }

  if (!isBusinessMember()) {
    return (
      <PageShell width="full" className="business-dashboard">
        <EmptyState
          icon={faStore}
          title="Business Access Required"
          description="The Business Dashboard is available to Guild businesses. List your business free with a Crew Listing to manage your listings and track performance — or upgrade for promotion and premium placement."
          actions={[
            { label: 'List Your Business Free', onClick: () => navigate('/for-businesses'), primary: true },
            { label: 'View Subscription Options', onClick: () => navigate('/account/subscriptions') },
            { label: 'Go Home', onClick: () => navigate('/') },
          ]}
        />
      </PageShell>
    );
  }

  // --- Tab content ---

  const renderTabContent = () => {
    if (!selectedListing) {
      return (
        <EmptyState
          icon={faStore}
          title="Select a Listing"
          description="Choose one of your listings from the sidebar to view its analytics, manage promotions, and respond to reviews."
        />
      );
    }

    switch (activeTab) {
      case 'overview':
        return (
          <div className="tab-content-overview">
            <OwnerDashboard
              listingId={selectedListing.id}
              ownerId={firebaseUser?.uid}
            />
            <div className="overview-grid">
              <div className="overview-section">
                <PerformanceSummary listingId={selectedListing.id} />
              </div>
              <div className="overview-section">
                <ListingTips listing={selectedListing} />
              </div>
            </div>
          </div>
        );

      case 'promotion':
        return (
          <PromotionStackPanel
            targetType="businessListing"
            targetId={selectedListing.id}
            target={selectedListing}
          />
        );

      case 'promotions-analytics':
        return (
          <PromotionsAnalytics
            userId={firebaseUser?.uid}
            listingId={selectedListing?.id}
            businessCategory={selectedListing?.category}
          />
        );

      case 'redemption-analytics':
        return (
          <RedemptionAnalytics
            businessId={selectedListing?.id}
          />
        );

      case 'reviews':
        return (
          <BusinessOwnerReviews
            businessId={selectedListing.id}
            businessName={selectedListing.name}
          />
        );

      case 'redeemables':
        return <RedeemableItemsAdmin businessId={selectedListing.id} />;

      case 'events':
        return <EventsManager
          businessId={selectedListing.id}
          listing={selectedListing}
          userListings={listings}
          prefill={eventEditorPrefill}
          onPrefillConsumed={() => setEventEditorPrefill(null)}
        />;

      case 'payments':
        return <PaymentsManager />;

      case 'bookings':
        return <BookingsManager businessId={selectedListing.id} />;

      case 'staff':
        return <StaffAdmin businessId={selectedListing.id} />;

      case 'actions':
        return (
          <OwnerActions
            listing={selectedListing}
            onEdit={() => handleEditListing(selectedListing)}
            onPromote={() => handleTabChange('promotion')}
            onViewAnalytics={() => handleTabChange('overview')}
            onDuplicate={() => handleDuplicate(selectedListing)}
            onArchive={() => handleArchive(selectedListing)}
          />
        );

      default:
        return null;
    }
  };

  return (
    <PageShell width="full" className="business-dashboard">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-content">
          <h1>
            <FontAwesomeIcon icon={faStore} />
            Business Dashboard
          </h1>
          <p className="header-subtitle">
            Manage your listings, track performance, and grow your business
          </p>
        </div>
        <div className="header-actions">
          <button
            className="btn btn-secondary"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <FontAwesomeIcon icon={faSync} spin={refreshing} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
          <button className="btn btn-primary" onClick={handleCreateListing}>
            <FontAwesomeIcon icon={faPlus} />
            New Listing
          </button>
        </div>
      </header>

      {error && (
        <div className="dashboard-error">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span>{error}</span>
          <button onClick={handleRefresh}>Try Again</button>
        </div>
      )}

      <div className="dashboard-content">
        {/* Listings Sidebar */}
        <aside className="listings-sidebar">
          <div className="sidebar-header">
            <h2>Your Listings</h2>
            <span className="listing-count">{listings.length}</span>
          </div>

          <div className="listings-list">
            {loading ? (
              <>
                <ListingCardSkeleton />
                <ListingCardSkeleton />
                <ListingCardSkeleton />
              </>
            ) : listings.length === 0 ? (
              <div className="no-listings">
                <FontAwesomeIcon icon={faAnchor} />
                <p>No listings yet, Captain!</p>
                <p className="no-listings-hint">
                  Create your first listing free, then upgrade to Featured Captain or Port Sponsor to promote it on the homepage and across the Guild.
                </p>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleCreateListing}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  Create Your First Listing
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={handleUpgradeTokens}
                  style={{ marginTop: '0.5rem' }}
                >
                  <FontAwesomeIcon icon={faCoins} />
                  Buy Doubloons
                </button>
              </div>
            ) : (
              listings.map((listing) => (
                <DashboardListingCard
                  key={listing.id}
                  listing={listing}
                  isSelected={selectedListing?.id === listing.id}
                  onSelect={handleSelectListing}
                  onEdit={handleEditListing}
                  onAddEvent={handleAddEvent}
                />
              ))
            )}
          </div>
        </aside>

        {/* Main Panel */}
        <main className="dashboard-main">
          {selectedListing && (
            <>
              <div className="selected-listing-header">
                <div className="listing-info">
                  <h2>{selectedListing.name}</h2>
                  <Link
                    to={`/business-listings/${selectedListing.id}`}
                    className="view-public-link"
                  >
                    View Public Listing
                    <FontAwesomeIcon icon={faArrowRight} />
                  </Link>
                </div>
                {selectedListing.promotion?.isActive && new Date(selectedListing.promotion?.expiresAt) > new Date() && (
                  <div className="active-promotion-banner">
                    <FontAwesomeIcon icon={faRocket} />
                    <span>
                      <strong>{PROMOTION_LABELS[selectedListing.promotion.type]}</strong> promotion active until{' '}
                      {new Date(selectedListing.promotion.expiresAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
                    </span>
                  </div>
                )}
              </div>

              <nav className="dashboard-tabs">
                <button
                  className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
                  onClick={() => handleTabChange('overview')}
                >
                  <FontAwesomeIcon icon={faChartLine} />
                  Analytics
                </button>
                <button
                  className={`tab-btn ${activeTab === 'promotion' ? 'active' : ''}`}
                  onClick={() => handleTabChange('promotion')}
                >
                  <FontAwesomeIcon icon={faBullhorn} />
                  Promotion
                  {tokenBalance > 0 && (
                    <span className="tab-token-hint">
                      <FontAwesomeIcon icon={faCoins} />
                      {tokenBalance}
                    </span>
                  )}
                </button>
                <button
                  className={`tab-btn ${activeTab === 'promotions-analytics' ? 'active' : ''}`}
                  onClick={() => handleTabChange('promotions-analytics')}
                >
                  <FontAwesomeIcon icon={faChartBar} />
                  Insights
                </button>
                <button
                  className={`tab-btn ${activeTab === 'redemption-analytics' ? 'active' : ''}`}
                  onClick={() => handleTabChange('redemption-analytics')}
                >
                  <FontAwesomeIcon icon={faHandHoldingHeart} />
                  Redemptions
                </button>
                <button
                  className={`tab-btn ${activeTab === 'reviews' ? 'active' : ''}`}
                  onClick={() => handleTabChange('reviews')}
                >
                  <FontAwesomeIcon icon={faCommentDots} />
                  Reviews
                </button>
                <button
                  className={`tab-btn ${activeTab === 'redeemables' ? 'active' : ''}`}
                  onClick={() => handleTabChange('redeemables')}
                >
                  <FontAwesomeIcon icon={faGift} />
                  Redeemables
                </button>
                <button
                  className={`tab-btn ${activeTab === 'staff' ? 'active' : ''}`}
                  onClick={() => handleTabChange('staff')}
                >
                  <FontAwesomeIcon icon={faIdBadge} />
                  Staff
                </button>
                <button
                  className={`tab-btn ${activeTab === 'events' ? 'active' : ''}`}
                  onClick={() => handleTabChange('events')}
                >
                  <FontAwesomeIcon icon={faCalendarAlt} />
                  Events
                </button>
                <button
                  className={`tab-btn ${activeTab === 'payments' ? 'active' : ''}`}
                  onClick={() => handleTabChange('payments')}
                >
                  <FontAwesomeIcon icon={faUniversity} />
                  Payments
                </button>
                <button
                  className={`tab-btn ${activeTab === 'bookings' ? 'active' : ''}`}
                  onClick={() => handleTabChange('bookings')}
                >
                  <FontAwesomeIcon icon={faReceipt} />
                  Bookings
                </button>
                <button
                  className={`tab-btn ${activeTab === 'actions' ? 'active' : ''}`}
                  onClick={() => handleTabChange('actions')}
                >
                  <FontAwesomeIcon icon={faCoins} />
                  Doubloon Offers
                </button>
              </nav>
            </>
          )}

          <div className="dashboard-tab-content">
            {loading ? (
              <div className="loading-state">
                <FontAwesomeIcon icon={faAnchor} spin />
                <p>Loading dashboard...</p>
              </div>
            ) : (
              renderTabContent()
            )}
          </div>
        </main>
      </div>
    </PageShell>
  );
};

export default BusinessDashboard;
